
import { Table, TableShape, RestaurantConfig, Zone, ZoneType, FloorDimensions, CategoryDefinition, WeekSchedule, Reservation } from './types';

const defaultDay = { open: "08:00", close: "23:00", isClosed: false };

const INITIAL_SCHEDULE: WeekSchedule = {
  Monday: { ...defaultDay },
  Tuesday: { ...defaultDay },
  Wednesday: { ...defaultDay },
  Thursday: { ...defaultDay },
  Friday: { ...defaultDay },
  Saturday: { open: "08:00", close: "22:00", isClosed: false },
  Sunday: { open: "09:00", close: "21:00", isClosed: false },
};

export const INITIAL_CONFIG: RestaurantConfig = {
  name: "SportArena Pro",
  address: "Sportallee 42, 12345 Hauptstadt",
  phoneLandline: "030 1234567",
  phoneMobile: "0170 9876543",
  contactEmail: "info@sportarena-pro.de",
  websiteUrl: "https://sportarena-pro.de",
  instagramUrl: "https://instagram.com",
  facebookUrl: "",
  twitterUrl: "",
  tiktokUrl: "",
  ownerName: "Alexander Sportowski",
  showOwnerOnStart: true,
  welcomeText: "Willkommen in der SportArena! Buche deine Spielfelder für Fußball, Tennis oder Badminton bequem online. Wir bieten modernste Böden und erstklassiges Equipment.",
  schedule: INITIAL_SCHEDULE,
  guestViewMode: 'SIMPLE',
  reservationMode: 'SPECIFIC_CATEGORY',
  lastReservationBuffer: 30,
  allowGuestReservations: true,
  requireReservationApproval: false,
  allowRecurringBookings: true, // Standardmäßig an
  recurringRequireApproval: true, // Serientermine brauchen oft manuelle Sichtung
  theme: {
    mode: 'MODERN',
    darkMode: false,
    primaryColor: '#10b981', // Emerald Green
    secondaryColor: '#064e3b', // Dark Green
    calendarConfirmedColor: '#064e3b',
    calendarPendingColor: '#f97316' // Orange-500
  },
  socialProviders: ['google', 'facebook'],
  packages: [
    {
      id: 'pkg-birthday',
      name: 'Kindergeburtstag Kickoff',
      icon: '🎂',
      categoryId: 'SOCCER_LARGE',
      allowedDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
      startTime: '10:00',
      endTime: '18:00',
      defaultPartySize: 12,
      durationMinutes: 120
    },
    {
      id: 'pkg-afterwork',
      name: 'After-Work Tennis & Drink',
      icon: '🎾',
      categoryId: 'TENNIS',
      allowedDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      startTime: '17:00',
      endTime: '22:00',
      defaultPartySize: 4,
      durationMinutes: 90
    },
    {
      id: 'pkg-weekend',
      name: 'Weekend Power Badminton',
      icon: '⚡',
      categoryId: 'BADMINTON',
      allowedDays: ['Saturday', 'Sunday'],
      startTime: '08:00',
      endTime: '20:00',
      defaultPartySize: 4,
      durationMinutes: 180
    }
  ]
};

export const DEFAULT_CATEGORIES: CategoryDefinition[] = [
  { id: 'SOCCER_LARGE', name: 'Fußball (Groß)', color: '#ecfdf5', borderColor: '#10b981', icon: '⚽', minDuration: 90, defaultSeats: 12 },
  { id: 'SOCCER_SMALL', name: 'Fußball (Klein)', color: '#f0fdf4', borderColor: '#22c55e', icon: '⚽', minDuration: 60, defaultSeats: 6 },
  { id: 'TENNIS', name: 'Tennis', color: '#eff6ff', borderColor: '#3b82f6', icon: '🎾', minDuration: 60, defaultSeats: 4 },
  { id: 'BADMINTON', name: 'Badminton', color: '#fff7ed', borderColor: '#3b82f6', icon: '🏸', minDuration: 45, defaultSeats: 4 },
  { id: 'PADEL', name: 'Padel Tennis', color: '#f5f3ff', borderColor: '#8b5cf6', icon: '🎾', minDuration: 60, defaultSeats: 4 },
  { id: 'GYM', name: 'Kursraum', color: '#fff1f2', borderColor: '#f43f5e', icon: '🏋️', minDuration: 60, defaultSeats: 20 },
];

export const INITIAL_DIMENSIONS: FloorDimensions = {
  width: 1000,
  height: 800
};

export const INITIAL_ZONES: Zone[] = [
  { id: 'z1', name: 'Spielfeld-Halle 1', type: ZoneType.SPORT_AREA, x: 20, y: 20, width: 600, height: 500 },
  { id: 'z2', name: 'Umkleiden Damen', type: ZoneType.LOCKER_ROOM, x: 640, y: 20, width: 150, height: 200 },
  { id: 'z3', name: 'Umkleiden Herren', type: ZoneType.LOCKER_ROOM, x: 810, y: 20, width: 150, height: 200 },
  { id: 'z4', name: 'Sport-Lounge & Bar', type: ZoneType.LOUNGE, x: 640, y: 240, width: 320, height: 280 },
  { id: 'z5', name: 'Haupteingang', type: ZoneType.ENTRANCE, x: 20, y: 540, width: 200, height: 100 },
];

export const INITIAL_TABLES: Table[] = [
  { id: 't1', name: 'Court A (Fußball)', seats: 12, shape: TableShape.RECTANGLE, category: 'SOCCER_LARGE', x: 50, y: 50, width: 250, height: 150, rotation: 0 },
  { id: 't2', name: 'Court B (Fußball)', seats: 12, shape: TableShape.RECTANGLE, category: 'SOCCER_LARGE', x: 50, y: 220, width: 250, height: 150, rotation: 0 },
  { id: 't3', name: 'Tennis 1', seats: 4, shape: TableShape.RECTANGLE, category: 'TENNIS', x: 350, y: 50, width: 180, height: 100, rotation: 0 },
  { id: 't4', name: 'Tennis 2', seats: 4, shape: TableShape.RECTANGLE, category: 'TENNIS', x: 350, y: 170, width: 180, height: 100, rotation: 0 },
  { id: 't5', name: 'Badminton 1', seats: 4, shape: TableShape.RECTANGLE, category: 'BADMINTON', x: 350, y: 300, width: 80, height: 120, rotation: 0 },
  { id: 't6', name: 'Badminton 2', seats: 4, shape: TableShape.RECTANGLE, category: 'BADMINTON', x: 450, y: 300, width: 80, height: 120, rotation: 0 },
];

// Dynamisch generierte Dummy-Reservierungen für HEUTE
const todayStr = new Date().toISOString().split('T')[0];

export const INITIAL_RESERVATIONS: Reservation[] = [
  // --- Court A ---
  { id: 'res-1', tableId: 't1', categoryId: 'SOCCER_LARGE', customerName: 'FC Turbo Kickers', partySize: 12, date: todayStr, time: '17:30', durationMinutes: 90, status: 'CONFIRMED', email: 'info@turbo-kickers.de', phone: '0123-456789' },
  { id: 'res-a2', tableId: 't1', categoryId: 'SOCCER_LARGE', customerName: 'Jugendtraining U14', partySize: 10, date: todayStr, time: '15:00', durationMinutes: 90, status: 'CONFIRMED', email: 'jugend@sport-ev.de' },
  { id: 'res-a3', tableId: 't1', categoryId: 'SOCCER_LARGE', customerName: 'Senior Soccer Club', partySize: 8, date: todayStr, time: '10:00', durationMinutes: 120, status: 'CONFIRMED', phone: '0171-8899112' },
  
  // --- Court B ---
  { id: 'res-5', tableId: 't2', categoryId: 'SOCCER_LARGE', customerName: 'Betriebssport Gruppe A', partySize: 10, date: todayStr, time: '20:00', durationMinutes: 90, status: 'CONFIRMED', email: 'hr@corp-a.com' },
  { id: 'res-b2', tableId: 't2', categoryId: 'SOCCER_LARGE', customerName: 'Freizeitkicker e.V.', partySize: 12, date: todayStr, time: '18:00', durationMinutes: 90, status: 'CONFIRMED' },
  
  // --- Tennis 1 ---
  { id: 'res-2', tableId: 't3', categoryId: 'TENNIS', customerName: 'Boris Becker jr.', partySize: 2, date: todayStr, time: '10:00', durationMinutes: 60, status: 'CONFIRMED', email: 'boris@jr-tennis.de' },
  { id: 'res-t1-2', tableId: 't3', categoryId: 'TENNIS', customerName: 'T. Müller (Einzel)', partySize: 2, date: todayStr, time: '14:00', durationMinutes: 60, status: 'CONFIRMED' },
  { id: 'res-t1-3', tableId: 't3', categoryId: 'TENNIS', customerName: 'Doppel-Spaß 40+', partySize: 4, date: todayStr, time: '19:00', durationMinutes: 120, status: 'CONFIRMED', notes: 'Platz 1 bevorzugt' },
  
  // --- Tennis 2 ---
  { id: 'res-3', tableId: 't4', categoryId: 'TENNIS', customerName: 'Steffi Graf Fanclub', partySize: 4, date: todayStr, time: '15:30', durationMinutes: 90, status: 'CONFIRMED', email: 'fans@graf-forever.de' },
  { id: 'res-t2-2', tableId: 't4', categoryId: 'TENNIS', customerName: 'Tennis Academy', partySize: 2, date: todayStr, time: '09:00', durationMinutes: 120, status: 'CONFIRMED' },
  
  // --- Badminton 1 ---
  { id: 'res-4', tableId: 't5', categoryId: 'BADMINTON', customerName: 'Lin Dan Academy', partySize: 2, date: todayStr, time: '17:00', durationMinutes: 45, status: 'CONFIRMED', phone: '089-12345' },
  { id: 'res-ba1-2', tableId: 't5', categoryId: 'BADMINTON', customerName: 'Speedy Birds', partySize: 4, date: todayStr, time: '18:00', durationMinutes: 60, status: 'CONFIRMED' },
  { id: 'res-ba1-3', tableId: 't5', categoryId: 'BADMINTON', customerName: 'Family Fun', partySize: 3, date: todayStr, time: '11:00', durationMinutes: 60, status: 'CONFIRMED' },

  // --- Badminton 2 ---
  { id: 'res-ba2-1', tableId: 't6', categoryId: 'BADMINTON', customerName: 'Night Smashers', partySize: 2, date: todayStr, time: '21:00', durationMinutes: 45, status: 'CONFIRMED' },
  { id: 'res-ba2-2', tableId: 't6', categoryId: 'BADMINTON', customerName: 'Badminton Profis', partySize: 4, date: todayStr, time: '16:00', durationMinutes: 60, status: 'CONFIRMED', email: 'pro@badminton.net' }
];
